import React, { useState } from 'react';
import { X, Sparkles, FolderPlus } from 'lucide-react';

export default function NewProjectModal({ team, onClose, onAddProject }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('$50,000');
  const [dueDate, setDueDate] = useState('2025-08-30');
  const [priority, setPriority] = useState('High');
  const [lead, setLead] = useState(team[0]?.id || '');
  const [category, setCategory] = useState('Infrastructure');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const newProj = {
      id: 'proj-' + Date.now(),
      name: name.trim(),
      key: key.trim().toUpperCase() || name.substring(0, 3).toUpperCase(),
      description: description.trim(),
      status: 'Planning',
      progress: 5,
      dueDate,
      budget,
      priority,
      lead,
      category,
      color: 'indigo'
    };

    onAddProject(newProj);
    onClose();
  };

  const handleAiTemplate = () => {
    setName('Autonomous AI Agent Orchestration Suite');
    setKey('AIA');
    setDescription('Build multi-agent LLM workflow runner with human-in-the-loop validation checkpoints.');
    setBudget('$95,000');
    setPriority('Urgent');
    setCategory('AI & ML');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-slate-100 flex flex-col">
        
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-bold text-slate-900">Create New Project</h2>
            <button 
              type="button"
              onClick={handleAiTemplate}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold rounded-lg transition"
            >
              <Sparkles className="w-3.5 h-3.5" /> AI Template
            </button>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Project Name</label>
              <input 
                type="text" 
                required
                placeholder="e.g. Nexus NextGen"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!key && e.target.value) {
                    setKey(e.target.value.substring(0, 3).toUpperCase());
                  }
                }}
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Project Key</label>
              <input 
                type="text" 
                required
                maxLength={5}
                placeholder="NEX"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono uppercase"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Description</label>
            <textarea 
              rows={3}
              placeholder="Outline project objectives and scope..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Project Lead</label>
              <select 
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {team.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Category</label>
              <select 
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Infrastructure">Infrastructure</option>
                <option value="Mobile">Mobile</option>
                <option value="AI & ML">AI & ML</option>
                <option value="Web">Web</option>
                <option value="Security">Security</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Budget</label>
              <input 
                type="text"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Target Due Date</label>
              <input 
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Priority</label>
              <select 
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Urgent">Urgent</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={onClose}
              className="px-5 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-xl hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-5 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/20 flex items-center gap-2"
            >
              <FolderPlus className="w-4 h-4" /> Create Project
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
